/**
 * @file src/api/auth.api.js
 * @description Authentication API calls only.
 *
 * Responsibilities:
 *  - Login, signup, OTP verification, logout
 *  - Authenticated user fetch
 *
 * Company, selected-company context, and audit-log API calls live in:
 *  - src/api/company.api.js
 *
 * Rules:
 *  - No React, no hooks, no state
 *  - Every function is async and returns raw data or throws ApiError
 *  - ApiError is thrown automatically by the axios interceptor in lib/axios.js
 *  - No console.log in production code
 *  - withCredentials is set globally on apiClient
 */

import { apiClient } from "lib/axios";

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/**
 * Log in with email and password.
 * Backend sets the auth httpOnly cookie.
 *
 * @param {{ email: string, password: string }} credentials
 * @returns {Promise<object>} User/session response, possibly with nextStep
 */
export const loginUser = async ({ email, password }) => {
  const { data } = await apiClient.post("/auth/login", { email, password });
  return data;
};

/**
 * Initiate signup — sends OTP to the provided email.
 *
 * @param {{ email: string }} payload
 * @returns {Promise<object>}
 */
export const initiateSignup = async ({ email }) => {
  const { data } = await apiClient.post("/auth/signup", { email });
  return data;
};

/**
 * Verify OTP and complete signup.
 * Backend sets the auth httpOnly cookie.
 *
 * @param {{
 *   email: string,
 *   otp: string,
 *   password: string,
 *   phone: string,
 *   name: string
 * }} payload
 * @returns {Promise<object>} User/session response, possibly with nextStep
 */
export const verifyOtpAndCompleteSignup = async ({
  email,
  otp,
  password,
  phone,
  name,
}) => {
  const { data } = await apiClient.post("/auth/verify-otp", {
    email,
    otp,
    password,
    phone,
    name,
  });

  return data;
};

/**
 * Log out the current user — clears cookies server-side.
 *
 * @returns {Promise<object>}
 */
export const logoutUser = async () => {
  const { data } = await apiClient.post("/auth/logout", {});
  return data;
};

/**
 * Fetch the currently authenticated user profile.
 * Used by React Query as the source of truth for auth state.
 *
 * @returns {Promise<object>} Authenticated user/session response
 */
export const getAuthenticatedUser = async () => {
  const { data } = await apiClient.get("/auth/me");
  return data;
};
